import { Service, inject } from '@angular/core';

import { ApiService } from './api.service';
import { SnackBarService } from './snackbar.service';
import { Registru, ActIdTip, Judet, Tara, Facultate, FacultateDocTip, PpuTip, Ppu, PpuGrad, PpuDoc, Titlu } from '../models/registre.model';

import { environment } from '../../../environments/environment';
import { cautareText } from '../forms/campuri';
import { RegistreStore } from '../state/registre.store';
import { executa } from './executa';

@Service()
export class RegistreService {
    private readonly registreStore = inject(RegistreStore);
    private apiService = inject(ApiService);
    private snackBar = inject(SnackBarService);

    get registru(): Registru {
        return this.registreStore.registru();
    }

    getJudet() {
        return executa(this.apiService.getJudeteList(), response => {
            this.registreStore.setJudete(response);
        }, this.snackBar);
    }

    getRegistre() {
        return executa(this.apiService.getRegistre(), (response: Registru) => {
            this.registreStore.setTari(response.tara);
            this.registreStore.setActIdTip(response.act_id_tip);
            this.registreStore.setFacultate(response.facultate);
            this.registreStore.setFacDocTip(response.facultate_doc_tip);
            this.registreStore.setPpu(response.ppu);
            this.registreStore.setPpuTip(response.ppu_tip);
            this.registreStore.setPpuGrad(response.ppu_grad);
            this.registreStore.setPpuDoc(response.ppu_doc);
            this.registreStore.setPpuGrup(response.ppu_grup);
            this.registreStore.setPpuDoc(response.ppu_doc);
            this.registreStore.setTitlu(response.titlu);
            this.registreStore.setAdresaTip(response.adresa_tip);
            this.registreStore.setAsigurator(response.asigurator);
            this.registreStore.setCmj(response.cmj);
            this.registreStore.setMedicStatus(response.medic_status);
            this.registreStore.setDocTip(response.doc_tip);
            this.registreStore.setDocStatus(response.doc_status);
            this.registreStore.setJudete(response.judet);
            this.registreStore.setMedicActiuniTip(response.actiuni_tip);
            this.registreStore.setCmjServicii(response.cmj_servicii);
            this.registreStore.setEmcTip(response.emc_tip);
        }, this.snackBar);
    }

    listaJudete() {
        return this.registru.judet;
    }

    listaJudeteFaraDiverse() {
        const filterOut = [141, 142, 143, 144, 145, 146, 160, 161];
        return this.registru.judet.filter(function (item) {
            return filterOut.indexOf(item.id) === -1;
        });
    }

    filterJudete(nume: string): Judet[] {
        return this.registru.judet
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayJudet() {
        return (val: unknown) => {
            if (val && this.registru.judet) {
                return this.registru.judet.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaTara() {
        return this.registru.tara;
    }

    filterTara(nume: string): Tara[] {
        return this.registru.tara
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayTara() {
        return (val: unknown) => {
            if (val && this.registru.tara) {
                return this.registru.tara.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaIdCardTip() {
        return this.registru.act_id_tip;
    }

    filterIdCardTip(nume: string): ActIdTip[] {
        return this.registru.act_id_tip
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayIdCardTip() {
        return (val: unknown) => {
            if (val && this.registru.act_id_tip) {
                return this.registru.act_id_tip.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaFacultate() {
        return this.registru.facultate;
    }

    filterFacultate(nume: string): Facultate[] {
        return this.registru.facultate
            .filter(option =>
                cautareText(nume).test(option.facultate)
            );
    }

    get displayFacultate() {
        return (val: unknown) => {
            if (val && this.registru.facultate) {
                return this.registru.facultate.find(item => item.id === val)?.facultate ?? '';
            } else {
                return '';
            }
        };
    }

    listaFacDocTip() {
        return this.registru.facultate_doc_tip;
    }

    filterFacDocTip(nume: string): FacultateDocTip[] {
        return this.registru.facultate_doc_tip
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayFacDocTip() {
        return (val: unknown) => {
            if (val && this.registru.facultate_doc_tip) {
                return this.registru.facultate_doc_tip.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaPpuTip() {
        return this.registru.ppu_tip;
    }

    filterPpuTip(nume: string): PpuTip[] {
        return this.registru.ppu_tip
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayPpuTip() {
        return (val: unknown) => {
            if (val && this.registru.ppu_tip) {
                return this.registru.ppu_tip.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaPpu() {
        return this.registru.ppu;
    }

    filterPpu(nume: string): Ppu[] {
        return this.registru.ppu
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    filterPpuNumeTip(nume: string, tip: number): Ppu[] {
        if (tip === 2) {
            return this.registru.ppu
                .filter(item => item.tip_id === tip)
                .filter(option =>
                    cautareText(nume).test(option.nume)
                );
        } else {
            return this.registru.ppu
                .filter(item => item.tip_id !== 2)
                .filter(option =>
                    cautareText(nume).test(option.nume)
                );
        }
    }

    get displayPpu() {
        return (val: unknown) => {
            if (val && this.registru.ppu) {
                return this.registru.ppu.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaPpuGrad() {
        return this.registru.ppu_grad.filter(item => item.id !== 9);
    }

    filterPpuGrad(nume: string): PpuGrad[] {
        return this.registru.ppu_grad
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayPpuGrad() {
        return (val: unknown) => {
            if (val && this.registru.ppu_grad) {
                return this.registru.ppu_grad.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaPpuDocTip() {
        return this.registru.ppu_doc;
    }

    filterPpuDocTip(nume: string): PpuDoc[] {
        return this.registru.ppu_doc
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayPpuDocTip() {
        return (val: unknown) => {
            if (val && this.registru.ppu_doc) {
                return this.registru.ppu_doc.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaTitluNume() {
        return this.registru.titlu;
    }

    filterTitluNume(nume: string): Titlu[] {
        return this.registru.titlu
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayTitluNume() {
        return (val: unknown) => {
            if (val && this.registru.titlu) {
                return this.registru.titlu.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    get displayTitluTip() {
        return (val: unknown) => {
            if (val && this.registru.titlu) {
                return this.registru.titlu.find(item => item.id === val)?.tip ?? '';
            } else {
                return '';
            }
        };
    }

    listaAdresaTip() {
        return this.registru.adresa_tip;
    }

    filterAdresaTip(nume: string) {
        return this.registru.adresa_tip
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayAdresaTip() {
        return (val: unknown) => {
            if (val && this.registru.adresa_tip) {
                return this.registru.adresa_tip.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaDocTip() {
        return this.registru.doc_tip;
    }

    filterDocTip(nume: string) {
        return this.registru.doc_tip
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    listaDocTipCert() {
        return this.registru.doc_tip.filter(
            ({ id }) => environment.listaDocTipCertificat.includes(id)
        );
    }

    filterDocTipCertificat(nume: string) {
        return this.registru.doc_tip
            .filter(
                ({ id }) => environment.listaDocTipCertificat.includes(id)
            )
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayDocTip() {
        return (val: unknown) => {
            if (val && this.registru.doc_tip) {
                return this.registru.doc_tip.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaDocStatus() {
        return this.registru.doc_status;
    }

    filterDocStatus(nume: string) {
        return this.registru.doc_status
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayDocStatus() {
        return (val: unknown) => {
            if (val && this.registru.doc_status) {
                return this.registru.doc_status.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaAsigurator() {
        return this.registru.asigurator;
    }

    filterAsigurator(nume: string) {
        return this.registru.asigurator
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayAsigurator() {
        return (val: unknown) => {
            if (val && this.registru.asigurator) {
                return this.registru.asigurator.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaMedicStatus() {
        return this.registru.medic_status;
    }

    filterMedicStatus(nume: string) {
        return this.registru.medic_status
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayMedicStatus() {
        return (val: unknown) => {
            if (val && this.registru.medic_status) {
                return this.registru.medic_status.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    get iconMedicStatus() {
        return (val: unknown) => {
            return this.registru.medic_status.find(item => item.id === val)?.icon ?? '';
        };
    }

    listaMedicActiuniTip() {
        return this.registru.actiuni_tip;
    }

    filterMedicActiuniTip(nume: string) {
        return this.registru.actiuni_tip
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayMedicActiuniTip() {
        return (val: unknown) => {
            if (val && this.registru.actiuni_tip) {
                return this.registru.actiuni_tip.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaCmjServicii(userJudet?: number | null) {
        if (userJudet) {
            if (userJudet !== 161) {
                return this.registru.cmj_servicii
                    .filter(item => item.activ === true)
                    .filter(
                        item => (item.judet_id === userJudet || item.judet_id === 161)
                    )
                    .sort((a,b) => (b.judet_id - a.judet_id))
                    .slice();
            }
        }
        return this.registru.cmj_servicii.filter(item => item.activ === true).slice();
    }

    filterCmjServicii(nume: string, judet_id: number | null) {
        if (judet_id !== 160) {
            return this.registru.cmj_servicii
                .filter(item => item.activ === true)
                .filter(item => item.judet_id === judet_id)
                .filter(option =>
                    cautareText(nume).test(option.nume)
                ).slice();
        } else {
            return this.registru.cmj_servicii
                .filter(item => item.activ === true)
                .filter(option =>
                    cautareText(nume).test(option.nume)
                ).slice();
        }
    }

    get displayCmjServicii() {
        return (val: unknown) => {
            if (val && this.registru.cmj_servicii) {
                return this.registru.cmj_servicii.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    listaEmcTip() {
        return this.registru.emc_tip;
    }

    filterEmcTip(nume: string) {
        return this.registru.emc_tip
            .filter(option =>
                cautareText(nume).test(option.nume)
            );
    }

    get displayEmcTip() {
        return (val: unknown) => {
            if (val && this.registru.emc_tip) {
                return this.registru.emc_tip.find(item => item.id === val)?.nume ?? '';
            } else {
                return '';
            }
        };
    }

    getEmcValabilitate(emcId: unknown): number {
        if (typeof (emcId) === 'number') {
            const selectedItem = this.registru.emc_tip.filter(
                item => item.id === emcId
            );
            return selectedItem[0].valabilitate;
        } else {
            return 0;
        }
    }

}
